import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import Stack from "@mui/material/Stack";
import {FormHelperText} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import {useTranslation} from "react-i18next";
import dateInputLanguage from "../hooks/dateInputLanguage";
import Cookies from "js-cookie";
import {
    CustomStyleDatePicker,
    CustomStyleDatePickerRequired,
    CustomStyleFormHelperText
} from "./Styles";

import {CalenderInputButton} from "./buttons/IconButtons";
import CustomDateTimePickerApp from "./CustomDateTimePicker/CustomDateTimePickerApp";
import {DATE_TIME_PICKER_MODEL} from "./CustomDateRangePicker/Model";
import {PERIOD_TIME_UNIT_ENUM} from "../enums/PeriodTimeUnitEnum";
import {getDatePickerFormat} from "../Apis/functions";
import React from "react";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 *
 * @Description DatePicker
 * @param String id: Identifier for the DatePicker (required)
 * @param Boolean disabled: Enable/disable writing in the DatePicker (optional)
 * @param String value: Value to display under the DatePicker (required)
 * @param String name: Name for the DatePicker (optional)
 * @param Boolean emptyFields: Indicates if the field is empty (optional)
 * @param Boolean disableFuture: Prevent selection of future dates (optional)
 * @param Boolean disablePast: Prevent selection of past dates (optional)
 * @param String trans: Text for the label (required)
 * @param Boolean isValid: Show legend and input with error when isValid = false (optional)
 * @param String isValidText: Legend for input with error (optional)
 * @param Boolean required: DatePicker is required (optional)
 * @param Array views: Defines list of views to show in the dialog (optional)
 * @param String openTo: Defines the main view (optional)
 * @param Date minDate: Defines the minimum date available for selection (optional)
 * @param String helperText: Text for the legend, displayed under the DatePicker (optional)
 * @param Boolean onlySelecting: If onlySelecting === true, it does not allow entering numbers or arrows in the field (optional)
 * @param Event onError: onError trigger in the DatePicker (optional)
 * @param Event onChange: onChange trigger in the DatePicker (optional)
 * @returns {JSX.Element}
 */
export const CustomDatePicker = ({
    id,
    disabled,
    value,
    name,
    emptyFields,
    disableFuture = false,
    disablePast = false,
    trans,
    isValid = true,
    isValidText = "date_invalid",
    required = false,
    views = [PERIOD_TIME_UNIT_ENUM.YEARS.key, PERIOD_TIME_UNIT_ENUM.MONTHS.key, PERIOD_TIME_UNIT_ENUM.DAYS.key],
    openTo = PERIOD_TIME_UNIT_ENUM.YEARS.key,
    minDate = undefined,
    maxDate = undefined,
    helperText,
    onlySelecting = false,
    onError = () => { },
    onChange,
    noMarginBottom= false,
    onAccept,
    containerClasses=''
}) => {
    const lCode = Cookies.get('i18next') || 'en';
    const { t } = useTranslation();
    const shouldRemoveMargin = (helperText && !emptyFields && isValid);
    const {
        elementRef,
        calculateTop,
        handleKeyDown,
        getPopperStyles
    } = CustomDateTimePickerApp({onlySelecting, type: DATE_TIME_PICKER_MODEL.DATE});

    // Formats the date value using the current language settings.
    // This ensures disabled date fields display the same localized format
    // as the active DatePicker (e.g. MM/dd/yyyy for English, dd/MM/yyyy for Spanish/French).
    const dateFormat = getDatePickerFormat();

    return <>
        <div ref={elementRef} className={`position-relative ${containerClasses}`}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateInputLanguage(lCode)}
                                  localeText={{ fieldYearPlaceholder: (params) => 'A'.repeat(params.digitAmount) }}>
                <Stack spacing={3} >
                    <DatePicker
                        label={t(trans) + (required ? ' *' : '')}

                        format={ dateFormat}
                        sx={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? CustomStyleDatePickerRequired : CustomStyleDatePicker(noMarginBottom || shouldRemoveMargin)}
                        value={value ? new Date(value) : null}
                        onChange={onChange ? (e) => onChange(e, name) : undefined}
                        onAccept={onAccept ? (e) => onAccept(e, name) : undefined}
                        name={name}
                        openTo={openTo}
                        views={views}
                        className={((emptyFields && (value === '' || value === null)) || (!isValid && value !== '' && value !== null)) ? "input-required" : ""}
                        minDate={ minDate}
                        maxDate={ maxDate}
                        disableFuture={disableFuture}
                        disablePast={disablePast}
                        disabled={disabled}
                        defaultValue={null}
                        onError={(error) => onError(error)}
                        onOpen={() => calculateTop(DATE_TIME_PICKER_MODEL.DATE)}
                        slotProps={{
                            textField: {
                                 variant: 'outlined',
                                 id: id,
                                 onKeyDown: handleKeyDown,
                            },
                            popper: getPopperStyles()
                        }}
                        InputProps={{
                            endAdornment: (
                             <InputAdornment position="end">
                                 <CalenderInputButton/>
                             </InputAdornment>
                            ),
                        }}
                    />
                </Stack>
            </LocalizationProvider>
            {emptyFields && (value === '' || value === null) &&
                <FormHelperText
                    error={true}
                    sx={CustomStyleFormHelperText(false, noMarginBottom === false)}
                >
                    {t('requiredField')}
                </FormHelperText>
            }

            {!isValid && value !== '' &&
                <FormHelperText
                    error={true}
                    className={noMarginBottom ? 'h-0' : ''}
                    sx={CustomStyleFormHelperText(false, noMarginBottom === false)}
                >
                    {t(isValidText)}
                </FormHelperText>
            }

            { helperText && !emptyFields && isValid &&
                <FormHelperText
                    sx={CustomStyleFormHelperText(false, noMarginBottom === false )}
                >
                    {t(helperText)}
                </FormHelperText>
            }
        </div>
       </>
}
